import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import Notification from '../../../patterns/components/notification/notification';

export const EditCompanyLanguagesModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const initialCompanySettings = store.variables.companyInEditing;
    const defaultLanguageOptions = store.payload!.companySettingsPayload.companyAvailableLanguages.map((language) => ({label: `common.language.${language}`, value: language}));
    const companyId = store.payload!.companySettingsPayload.companyId;

    const [defaultLanguage, setDefaultLanguage] = useState<string>(initialCompanySettings ? initialCompanySettings.defaultLanguage : 'en');
    const [hasEnglish, setEnglish] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('en') : false);
    const [hasEstonian, setEstonian] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('et') : false);
    const [hasFinnish, setFinnish] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('fi') : false);
    const [hasLatvian, setLatvian] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('lv') : false);
    const [hasLithuanian, setLithuanian] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('lt') : false);
    const [hasSwedish, setSwedish] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('sv') : false);
    const [hasRussian, setRussian] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('ru') : false);
    const [hasPolish, setPolish] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('pl') : false);
    const [hasJapanese, setJapanese] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('ja') : false);
    const [hasVietnamese, setVietnamese] = useState<boolean>(initialCompanySettings ? initialCompanySettings.companyAvailableLanguages.includes('vi') : false);


    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{ store.label('company.company-language-modal.title')}</h4>
            </ModalHeader>
            <ModalContent>
                {store.variables.showValidation ?
                    <Notification text={store.variables.allLanguagesUnselected ? store.label('common.validation.all-languages-inactive') : store.label('common.validation.default-language-inactive') }
                        type={'info'}
                        defaultIsOpen={true}
                        className={'notifiaction__modal'}/> : undefined}
                <div className="text">
                    <Select
                        label={store.label('company.company-language-modal.select-language')}
                        defaultValue={defaultLanguage}
                        onChange={(value): void => setDefaultLanguage('' + value)}
                        options={
                            ...defaultLanguageOptions.map((language) => ({
                                label: store.label(`common.language.${language.value}`),
                                value: language.value,
                            }))}
                        id='company-select-language'
                        name='company-select-language'
                        type={undefined}
                    />
                    <h5>{ store.label('company.company-language-modal.sub-title')}</h5>
                    <Check
                        label={store.label('company.common.language.en')}
                        defaultChecked={hasEnglish}
                        onChange={(checked): void => setEnglish(checked)}
                        id='company-language-modal-en'
                        name='company-language-modal-en'
                        value='company-language-modal-en'
                    />
                    <Check
                        label={store.label('company.common.language.et')}
                        defaultChecked={hasEstonian}
                        onChange={(checked): void => setEstonian(checked)}
                        id='company-language-modal-et'
                        name='company-language-modal-et'
                        value='company-language-modal-et'
                    />
                    <Check
                        label={store.label('company.common.language.fi')}
                        defaultChecked={hasFinnish}
                        onChange={(checked): void => setFinnish(checked)}
                        id='company-language-modal-fi'
                        name='company-language-modal-fi'
                        value='company-language-modal-fi'
                    />
                    <Check
                        label={store.label('company.common.language.lv')}
                        defaultChecked={hasLatvian}
                        onChange={(checked): void => setLatvian(checked)}
                        id='company-language-modal-lv'
                        name='company-language-modal-lv'
                        value='company-language-modal-lv'
                    />
                    <Check
                        label={store.label('company.common.language.lt')}
                        defaultChecked={hasLithuanian}
                        onChange={(checked): void => setLithuanian(checked)}
                        id='company-language-modal-lt'
                        name='company-language-modal-lt'
                        value='company-language-modal-lt'
                    />
                    <Check
                        label={store.label('company.common.language.pl')}
                        defaultChecked={hasPolish}
                        onChange={(checked): void => setPolish(checked)}
                        id='company-language-modal-pl'
                        name='company-language-modal-pl'
                        value='company-language-modal-pl'
                    />
                    <Check
                        label={store.label('company.common.language.sv')}
                        defaultChecked={hasSwedish}
                        onChange={(checked): void => setSwedish(checked)}
                        id='company-language-modal-sv'
                        name='company-language-modal-sv'
                        value='company-language-modal-sv'
                    />
                    <Check
                        label={store.label('company.common.language.ru')}
                        defaultChecked={hasRussian}
                        onChange={(checked): void => setRussian(checked)}
                        id='company-language-modal-ru'
                        name='company-language-modal-ru'
                        value='company-language-modal-ru'
                    />
                    <Check
                        label={store.label('company.common.language.ja')}
                        defaultChecked={hasJapanese}
                        onChange={(checked): void => setJapanese(checked)}
                        id='company-language-modal-ja'
                        name='company-language-modal-ja'
                        value='company-language-modal-ja'
                    />
                    <Check
                        label={store.label('company.common.language.vi')}
                        defaultChecked={hasVietnamese}
                        onChange={(checked): void => setVietnamese(checked)}
                        id='company-language-modal-vi'
                        name='company-language-modal-vi'
                        value='company-language-modal-vi'
                    />
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white" text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>

                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveCompanyLanguageSettings({
                                companyId,
                                defaultLanguage,
                                hasEnglish,
                                hasEstonian,
                                hasFinnish,
                                hasLatvian,
                                hasLithuanian,
                                hasPolish,
                                hasSwedish,
                                hasRussian,
                                hasJapanese,
                                hasVietnamese,
                            })}/>

                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
